/**
 * Le tri-état de restauration — décision PURE, aucun effet (#1332).
 *
 * > **Un signal absent n'est pas un signal négatif.** « Je n'ai pas pu lire »
 * > et « il n'y a rien » rendaient jusqu'ici le même vide : dans les deux cas
 * > l'ancien parcours laissait le canvas vierge, et l'avocat lisait « ce fil n'a
 * > pas de brouillon » sur un fil qui en avait un.
 *
 * Trois issues, et une seule autorise une page blanche :
 *
 * | ce qui est arrivé | ce qu'on en conclut |
 * |---|---|
 * | recherche RÉUSSIE, `draft: null` | ce fil n'a pas de brouillon → ouverture vierge voulue |
 * | recherche en échec (réseau, 4xx, 5xx) | on ne sait pas → **ne rien poser**, le dire |
 * | brouillon trouvé, contenu illisible (**404 compris**) | restauration impossible → le dire |
 *
 * ⚠️ **Le 404 sur le CONTENU est le piège central.** Il ressemble à « rien à
 * restaurer » et n'en est pas : la recherche vient d'affirmer que ce brouillon
 * existe. Le traiter comme une absence rouvrirait le fil sur une page blanche
 * que la première frappe écraserait par-dessus le vrai brouillon — ou, pire,
 * sur l'original importé, c'est-à-dire une version antérieure à tout le travail
 * de l'avocat. Ni l'un ni l'autre : on n'ouvre rien et on l'explique.
 *
 * Aucun nouveau code HTTP ni migration : c'est l'interprétation côté client qui
 * change.
 */

import type { CanvasDraft } from '@hector/shared-types';

/** Ce que la recherche de brouillon a réellement établi. */
export type RechercheDeBrouillon =
  | { readonly issue: 'brouillon-trouve'; readonly draft: CanvasDraft }
  /** Réponse RÉUSSIE portant `draft: null` — la seule preuve d'absence. */
  | { readonly issue: 'aucun-brouillon' }
  | { readonly issue: 'recherche-illisible'; readonly cause: string };

/**
 * Fait durable : « la restauration de ce fil n'a pas pu aboutir ».
 *
 * Il ne vit pas dans un montage nullable — un montage détruit emporterait la
 * seule trace de l'échec et l'écran retomberait sur une page blanche muette.
 */
export interface RestaurationIndisponible {
  readonly conversationId: string;
  readonly cause: 'recherche-illisible' | 'contenu-illisible';
  /** Connu seulement quand la recherche avait déjà nommé le brouillon. */
  readonly draftId: string | null;
  /** Phrase montrée à l'avocat. */
  readonly message: string;
  /** Détail technique, pour la console et les rapports — jamais affiché tel quel. */
  readonly diagnostic: string;
}

export type DecisionApresRecherche =
  /** Ce fil n'a pas de brouillon : la page blanche est ce que l'avocat veut. */
  | { readonly geste: 'ouvrir-vierge' }
  | { readonly geste: 'telecharger-le-brouillon'; readonly draft: CanvasDraft }
  | { readonly geste: 'declarer-indisponible'; readonly etat: RestaurationIndisponible };

export const MESSAGE_RECHERCHE_ILLISIBLE =
  'Nous n’avons pas pu vérifier si cette conversation a un document enregistré : la recherche a '
  + 'échoué. Rien n’a été ouvert, et rien n’a été modifié. Réessayez dans quelques secondes ; '
  + 'si vous écrivez ici en attendant, ce sera dans un nouveau document.';

export const MESSAGE_CONTENU_ILLISIBLE =
  'Cette conversation a bien un document enregistré, mais son contenu n’a pas pu être téléchargé. '
  + 'Rien n’a été ouvert à la place : ni page blanche, ni version d’origine. Réessayez ; '
  + 'si l’échec persiste, signalez-le avant de retravailler ce document.';

/**
 * Première moitié du tri-état : ce que la recherche autorise.
 *
 * `switch` exhaustif sur l'union — pas d'`else` : une quatrième issue ajoutée
 * plus tard doit faire rougir la compilation, pas tomber dans la branche
 * « ouvrir vierge », qui est précisément la plus destructrice.
 */
export function deciderApresLaRecherche(
  conversationId: string,
  recherche: RechercheDeBrouillon,
): DecisionApresRecherche {
  switch (recherche.issue) {
    case 'aucun-brouillon':
      return { geste: 'ouvrir-vierge' };
    case 'brouillon-trouve':
      return { geste: 'telecharger-le-brouillon', draft: recherche.draft };
    case 'recherche-illisible':
      return {
        geste: 'declarer-indisponible',
        etat: {
          conversationId,
          cause: 'recherche-illisible',
          draftId: null,
          message: MESSAGE_RECHERCHE_ILLISIBLE,
          diagnostic:
            `Canvas draft lookup failed for conversationId=${conversationId}: ${recherche.cause}. `
            + 'No blank canvas and no original may be substituted; retry the lookup for this same conversation.',
        },
      };
  }
}

/**
 * Seconde moitié du tri-état : après découverte, **tout** échec de contenu est
 * une restauration impossible — 404 compris.
 *
 * ⚠️ **C'est un CONSTRUCTEUR, pas une décision, et c'est délibéré.** Il n'y a
 * rien à décider ici : le seul appelant est l'autorité du téléchargement (la
 * transaction de résidence), qui n'appelle que sur un échec **déjà constaté**.
 * La version précédente passait par une fonction à deux branches dont l'appelant
 * devait ensuite réfuter la branche « succès » par un `throw` inatteignable —
 * un intermédiaire qui n'ajoutait qu'un aiguillage mort.
 */
export function etatDeContenuIllisible(
  conversationId: string,
  draftId: string,
  cause: string,
): RestaurationIndisponible {
  return {
    conversationId,
    cause: 'contenu-illisible',
    draftId,
    message: MESSAGE_CONTENU_ILLISIBLE,
    diagnostic:
      `Canvas draft content download failed for draftId=${draftId} `
      + `conversationId=${conversationId}: ${cause}. `
      + 'The lookup already proved this draft exists, so a 404 here is an unreadable content, '
      + 'never an absence: do not open a blank canvas and do not fall back to the imported original.',
  };
}

/** Traduit un rejet de `getDraftByConversation` en issue de recherche. */
export function rechercheDepuisUnRejet(error: unknown): RechercheDeBrouillon {
  return {
    issue: 'recherche-illisible',
    cause: error instanceof Error ? error.message : String(error),
  };
}
